import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { BucketName } from "@/lib/storage";

/**
 * Hook to get a signed URL for private storage buckets
 * Automatically refreshes the signed URL before expiration
 */
export function useSignedStorageUrl(
  bucket: BucketName,
  path: string | null | undefined,
  expiresIn: number = 3600 // 1 hour default
): { url: string | null; loading: boolean; error: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    // If it's already a full URL (legacy), return as-is
    if (path.startsWith("http")) {
      setUrl(path);
      setLoading(false);
      return;
    }

    const fetchSignedUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: signError } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, expiresIn);

        if (signError) {
          throw signError;
        }

        setUrl(data?.signedUrl || null);
      } catch (err: any) {
        console.error("Error generating signed URL:", err);
        setError(err.message || "Failed to generate access URL");
        setUrl(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [bucket, path, expiresIn]);

  return { url, loading, error };
}
